import React, { useState, useEffect } from 'react';
// nodejs library to set properties for components
import PropTypes from 'prop-types';
// nodejs library that concatenates classes
import classNames from 'classnames';
// @mui/material components
import { makeStyles } from '@mui/styles';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import styles from 'assets/jss/material-kit-react/components/customInputStyle.js';

const useStyles = makeStyles(styles);

export default function CustomInput(props) {
  const classes = useStyles();
  const {
    formControlProps,
    labelText,
    id,
    labelProps,
    inputProps,
    error,
    white,
    inputRootCustomClasses,
    value: controlledValue,
    defaultValue,
    onKeyDown,
    disabledProp,
    success,
    onChange = () => {},
    fireWhileTyping = true,
    type = 'text',
    endAdornment,
    startAdornment,
  } = props;

  const [localValue, setLocalValue] = useState(controlledValue ?? defaultValue ?? '');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (fireWhileTyping && controlledValue !== undefined) {
      setLocalValue(controlledValue);
    }
  }, [controlledValue, fireWhileTyping]);

  const handleChange = (event) => {
    const val = event.target.value;
    if (fireWhileTyping) {
      onChange(event);
    } else {
      setLocalValue(val);
    }
  };

  const handleBlur = (event) => {
    if (!fireWhileTyping) {
      const syntheticEvent = {
        ...event,
        target: {
          ...event.target,
          value: localValue,
        },
      };
      onChange(syntheticEvent);
    }
    if (inputProps?.onBlur) {
      inputProps.onBlur(event);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const labelClasses = classNames({
    [' ' + classes.labelRootError]: error,
    [' ' + classes.labelRootSuccess]: success && !error,
  });

  const marginTop = classNames({
    [inputRootCustomClasses]: inputRootCustomClasses !== undefined,
  });

  const formControlClasses = formControlProps?.className
    ? classNames(formControlProps.className, classes.formControl)
    : classes.formControl;

  const inputValue = fireWhileTyping ? (controlledValue ?? '') : localValue;

  const renderPasswordToggle = () => {
    if (type === 'password') {
      return (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle password visibility"
            onClick={handleClickShowPassword}
            edge="end"
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      );
    }
    return null;
  };

  return (
    <FormControl {...formControlProps} className={formControlClasses}>
      <TextField
        label={labelText}
        variant="outlined"
        size="small"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        disabled={disabledProp}
        id={'input.' + id}
        type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
        InputProps={{
          startAdornment: startAdornment,
          endAdornment: endAdornment || renderPasswordToggle(),
          ...inputProps,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'transparent',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.5) !important',
              borderWidth: '1px !important',
            },
            '&:hover fieldset': {
              borderColor: '#FFD700 !important',
              borderWidth: '1px !important',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FFD700 !important',
              borderWidth: '2px !important',
            },
          },
          '& .MuiInputLabel-root': {
            top: 0,
            '&.Mui-focused': {
              color: '#FFD700 !important',
            },
          },
        }}
      />
    </FormControl>
  );
}

CustomInput.propTypes = {
  labelText: PropTypes.node,
  labelProps: PropTypes.object,
  id: PropTypes.string,
  inputProps: PropTypes.object,
  formControlProps: PropTypes.object,
  inputRootCustomClasses: PropTypes.string,
  error: PropTypes.bool,
  success: PropTypes.bool,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  white: PropTypes.bool,
  fireWhileTyping: PropTypes.bool,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  type: PropTypes.string,
  endAdornment: PropTypes.node,
  startAdornment: PropTypes.node,
};
